/**
 * Things resource — typed wrappers over `GET /v1/things[/{id}]` plus a
 * server→UI mapper.
 *
 * P2.2 vocabulary rename: a "Thing" is the unified data-source row
 * (appliance gateway, sensor child, or standalone/virtual source) that
 * replaced the split gateways/devices model in migration 0033. The routes
 * moved from `/v1/iot/gateways` + `/v1/iot/devices` + `/v1/iot/claim` to
 * `/v1/things*`; the old paths remain mounted as aliases for one release, so
 * this module is safe to flip in a single commit.
 *
 * Builds on the verb wrapper in `lib/api-client.ts` (which injects the
 * bearer token automatically — see lib/auth.tsx).
 */

import { api } from "@/lib/api-client";
import type { GatewayRecord } from "@/lib/iot/gateways-store";
import { qs } from "./qs";
import { collectAllPages } from "./paging";
import type { Thing, ThingLink, ThingList, ThingRates, UpdateFetchMode } from "./types";

/**
 * The 8-option `data_type` facade a tenant picks per tag row (P6.3). The
 * backend maps each to its internal data_kind + shape — the UI never sees
 * the lower-level taxonomy here.
 */
export type ThingDataType =
  | "number"
  | "integer"
  | "percentage"
  | "boolean"
  | "category"
  | "text"
  | "array"
  | "batch";

export const THING_DATA_TYPES: ThingDataType[] = [
  "number",
  "integer",
  "percentage",
  "boolean",
  "category",
  "text",
  "array",
  "batch",
];

/**
 * Register (word) order of a value that spans 2+ Modbus registers (int32 and
 * float32 are 2 registers, double is 4). Devices differ, and the wrong order
 * decodes a correct type into garbage. "big" (ABCD, high word first) is the
 * default and is never sent; "little" = DCBA (fully reversed), "cdab" = the
 * 16-bit words swapped, "badc" = the two bytes inside each word swapped.
 */
export type ModbusWordOrder = "big" | "little" | "cdab" | "badc";

export const MODBUS_WORD_ORDERS: ModbusWordOrder[] = ["big", "little", "cdab", "badc"];

/**
 * A wire `word_order` (tag read / template) → a known {@link ModbusWordOrder},
 * or undefined when unset or unrecognised. The gateway also accepts the letter
 * aliases "abcd" (= big) and "dcba" (= little), so those are folded in rather
 * than dropped: dropping one would silently reset the value to the big default
 * on the next save.
 */
export function parseWordOrder(v: string | null | undefined): ModbusWordOrder | undefined {
  const s = v?.trim().toLowerCase();
  if (!s) return undefined;
  if (s === "abcd") return "big";
  if (s === "dcba") return "little";
  return MODBUS_WORD_ORDERS.find((w) => w === s);
}

/**
 * A gateway *product* — a type of edge appliance the tenant can instantiate
 * as a Thing (P6 realignment). `field_protocol` is the OT-side protocol the
 * product speaks (set in the field app); `transport` is always MQTT (the
 * platform ingress). Mirrors `GET /v1/things/products`.
 */
export interface ThingProduct {
  id: string;
  label: string;
  field_protocol: string;
  transport: string;
  description: string;
  icon: string;
  default_poll_sec: number;
  allowed_shapes: string[];
  /**
   * Field driver ids this product may run (ids from `GET /v1/things/drivers`).
   * Empty for MQTT-native products, and authoritative over `field_protocol`:
   * a product with drivers polls devices, so it gets the instruments panel.
   * Optional only because a server older than the multi-protocol backend
   * omits it; read it through {@link productDrivers}.
   */
  drivers?: string[];
}

/** A product's driver ids, `[]` when the server did not send any. */
export function productDrivers(p: Pick<ThingProduct, "drivers"> | null | undefined): string[] {
  return Array.isArray(p?.drivers) ? p.drivers : [];
}

/** One authored tag row in the Add-a-Thing dialog (P6.3 body element). */
export interface ThingTagInput {
  name: string;
  data_type: ThingDataType;
  unit?: string;
  address?: string;
  poll_sec?: number;
  min?: number;
  max?: number;
  simulate?: boolean;
  /**
   * VSync Modbus round-trip fields (additive). Set by the instrument-template /
   * config-import path (`hintToTags` / `parseVsyncConfig`) so re-templating a
   * Thing or re-importing its `config.txt` preserves the wire addressing:
   * `p_id` = stable legacy param id (VSync `P_ID`, assigned server-side when
   * omitted), `p_type` = Modbus wire type (1/2 int, 3 long, 4 float),
   * `reg_count` = register count (1 int, 2 long/float). Omitted for plain rows.
   */
  p_id?: number;
  p_type?: number;
  reg_count?: number;
  /**
   * Marks the machine's `P_NO==1` cycle-counter param — the poll trigger the
   * device reads to detect a completed test, NOT a published reading. Exactly
   * one tag per VSync machine must set this, else the emitted config has no
   * `P_NO==1` and the device can't detect new tests.
   */
  is_cycle_counter?: boolean;
  /**
   * mig 0151 register-editor round-trip: the Modbus register space
   * (holding|input|coil|discrete) so a coil onboards + polls as a first-class
   * boolean, an explicit `data_kind`, and the linear transform the gateway
   * applies (real = raw*scale + offset). Omitted for a plain holding register.
   */
  kind?: "holding" | "input" | "coil" | "discrete";
  data_kind?: string;
  scale?: number;
  offset?: number;
  /**
   * Register order for a value spanning 2+ registers (see {@link ModbusWordOrder}).
   * Omitted for the "big" default. A tags:replace never carries the instrument's
   * link (protocol / slave / ip / port / serial): the backend keeps it as is.
   */
  word_order?: ModbusWordOrder;
}

/** `POST /v1/things` body (P6.3 pinned contract). */
export interface CreateThingBody {
  product_id: string;
  site_id?: string;
  name: string;
  simulate: boolean;
  tags: ThingTagInput[];
}

/** `POST /v1/things` response — the Thing plus its rolled-up tag count. */
export interface CreatedThing extends Thing {
  tag_count: number;
}

/**
 * One tag row on a Thing — the wire shape of `GET /v1/things/{id}/tags` items
 * (mirrors the backend iot.Tag). `data_kind` drives the data-entry input: a
 * numeric scalar publishes a numeric reading, a discrete kind publishes a
 * value_text reading.
 */
export interface ThingTag {
  id: string;
  device_id: string;
  name: string;
  description: string;
  unit: string;
  min_value?: number;
  max_value?: number;
  data_kind: string;
  semantic_tag?: string | null;
  source: string;
  created_at: string;
  /**
   * Modbus wire + register metadata echoed by `GET /v1/things/{id}/tags` for an
   * instrument child (nullable for plain tags). The register editor seeds its
   * rows from these so re-opening a tag preserves its address, register count,
   * register space (a coil stays a coil) and scale instead of resetting them.
   */
  address?: string | null;
  /** This point's own poll interval in seconds (each tag stores its own). */
  poll_sec?: number | null;
  /** Publish only when the value moves more than this; omitted when unset. */
  deadband?: number | null;
  /**
   * The point's EFFECTIVE heartbeat in seconds (its own, else the default for a point with a deadband); omitted when
   * it has none. The gateway re-sends a value the deadband holds back once the point has been silent this long.
   */
  max_silence_sec?: number | null;
  /** True when `max_silence_sec` is the point's own, not the default. */
  max_silence_own?: boolean;
  /** False when the gateway leaves the point out (kept with its history, not read); missing means true. */
  acquire?: boolean;
  p_id?: number | null;
  p_type?: number | null;
  reg_count?: number | null;
  is_cycle_counter?: boolean | null;
  register_space?: "holding" | "input" | "coil" | "discrete" | null;
  scale?: number | null;
  reg_offset?: number | null;
  /**
   * The instrument's Modbus link, echoed on each of its tags (one instrument has
   * one link). `protocol` is the legacy code: 1 = RTU, 2 = TCP. TCP uses
   * `ip_addr` + `port`; RTU uses `serial_port` + `baud` / `data_bits` / `parity` /
   * `stop_bits`. `word_order` is this tag's register order (null = the "big"
   * default); narrow it with {@link parseWordOrder}.
   */
  protocol?: number | null;
  slave_id?: number | null;
  ip_addr?: string | null;
  port?: number | null;
  serial_port?: string | null;
  baud?: number | null;
  data_bits?: number | null;
  parity?: string | null;
  stop_bits?: number | null;
  word_order?: string | null;
  thing_name?: string | null;
  thing_slug?: string | null;
}

/** One column of the shift-readings sheet — an instrument tag (metric). */
export interface ShiftReadingColumn {
  name: string;
  unit: string;
  data_kind: string;
}

/**
 * One cycle row of the shift-readings sheet: a reading timestamp plus that
 * cycle's per-tag values, keyed by tag name (matching a column's `name`). A tag
 * with no reading at this instant is absent from `values`.
 */
export interface ShiftReadingRow {
  t: string;
  values: Record<string, number>;
}

/**
 * `GET /v1/things/{id}/readings-sheet` — a raw one-row-per-cycle readings sheet
 * for one instrument over a window, optionally one shift of the plant's own
 * calendar (`shift_label`). Parity with the legacy VSync ReportController.php
 * historic/shift readings.
 */
export interface ShiftReadingsSheet {
  thing_id: string;
  slug: string;
  name: string;
  from: string;
  to: string;
  shift: ShiftFilter;
  tz: string;
  /** The calendar shift the sheet holds, set when the request named one (`shift_label`); absent from a server that
   *  predates it, which ignores the parameter and sends every cycle. */
  shift_label?: string;
  columns: ShiftReadingColumn[];
  rows: ShiftReadingRow[];
  row_limit: number;
  capped: boolean;
}

/**
 * The sheet's fixed partition: everything, or a day band (06:00 to 18:00 in
 * `tz`) and the night. The UI sends "all" only: a plant's own shifts go by
 * `shift_label`, on its calendar. day and night stay for older callers.
 */
export type ShiftFilter = "all" | "day" | "night";

/** The readings-sheet query: the window, and at most one shift filter. */
export type ReadingsSheetParams = {
  from?: string;
  to?: string;
  shift?: ShiftFilter;
  /** One shift of the plant's calendar by its label (GET /v1/twin/plant-time), cut on the plant's clock by the server. */
  shift_label?: string;
  /** The zone the fixed day/night band is cut in. */
  tz?: string;
  limit?: number;
};

/** One Modbus param row for a new instrument (`POST /v1/things/{gw}/instruments`). */
export interface InstrumentParamInput {
  metric: string;
  register: string;
  /** Modbus register space the gateway polls this from: "holding" (default) | "input" |
   *  "coil" | "discrete". A coil/discrete is ingested as a boolean state AND drives the
   *  predicate edge (mig 0151 / ParamConfig.Kind). */
  kind?: "holding" | "input" | "coil" | "discrete";
  reg_count?: number;
  data_type?: "int16" | "int32" | "float32" | "double";
  /** Semantic tag kind the backend creates (analog_scalar | count_scalar | boolean_state |
   *  ...); default derived from kind. */
  data_kind?: string;
  /** Linear transform the gateway applies before publishing: real = raw*scale + offset. */
  scale?: number;
  offset?: number;
  is_cycle_counter?: boolean;
  /** Register order when the value spans 2+ registers (int32 / float32 / double).
   *  Omit for the "big" (ABCD) default. */
  word_order?: ModbusWordOrder;
  /** This point's poll interval, whole seconds 1 to 86400; omitted means the body's `poll_sec`. */
  poll_sec?: number;
}

/**
 * `POST /v1/things/{gw}/instruments` body — add an instrument to a VSync gateway
 * as a CHILD sensor Thing with its Modbus register map. `protocol`/`slave_id` and
 * the link fields are the instrument's single Modbus connection; each param
 * carries its own register. Backend assigns the next `machine_ref` and chains a
 * `config_push`.
 *
 * Link fields per transport (omit any that are unset):
 * - tcp: `ip`, which is required (the backend 400s "ip is required for a tcp
 *   instrument" without it), + `port` (default 502).
 * - rtu: `serial_port`, the host serial device a PC / Pi gateway opens (e.g.
 *   "COM3", "/dev/ttyUSB0"; a VSync box ignores it), + the line settings `baud`
 *   (default 9600), `data_bits` (8), `parity` ("N"), `stop_bits` (1).
 */
export interface CreateInstrumentBody {
  name: string;
  equipment_type?: string;
  protocol: "rtu" | "tcp";
  slave_id?: number;
  ip?: string;
  port?: number;
  serial_port?: string;
  baud?: number;
  data_bits?: 7 | 8;
  parity?: "N" | "E" | "O";
  stop_bits?: 1 | 2;
  poll_sec?: number;
  params: InstrumentParamInput[];
}

/** `POST /v1/things/{gw}/instruments` response. */
export interface CreatedInstrument {
  instrument_id: string;
  slug: string;
  machine_ref: number;
  params: number;
  config_pushed: boolean;
  /** The instrument's driver (v2 bodies). */
  driver?: string;
  /** Which write-only link secrets were stored (v2 bodies). */
  secrets_set?: string[];
  /** e.g. "gateway_not_announced": the gateway has not sent a birth yet. */
  warnings?: string[];
}

/** The data types a v2 point is decoded as (one enum across drivers; a driver lists the ones it can decode). */
export type PointDataType =
  | "bool"
  | "int16"
  | "uint16"
  | "int32"
  | "uint32"
  | "int64"
  | "uint64"
  | "float32"
  | "float64"
  | "string";

/**
 * One v2 point (`InstrumentParam`, v2 fields only). `address` is the driver's
 * address_schema object ({space, register, ...} for Modbus, {node_id} for OPC
 * UA, {area, db, byte, bit} for S7, ...). Mixing these with the v1 fields
 * (register / kind / reg_count / word_order) is a 400 `mixed_body`, so the two
 * shapes are separate types.
 */
export interface InstrumentPointV2 {
  metric: string;
  address: Record<string, unknown>;
  data_type: PointDataType;
  /** Publish only when the value moves more than this. On PATCH, omitted keeps the point's; 0 clears it. */
  deadband?: number;
  /**
   * This point's poll interval, whole seconds 1 to 86400. Omitted on create means
   * the body's `poll_sec`; on PATCH it keeps the point's, and it wins over the body's.
   */
  poll_sec?: number;
  /** A `string` point needs `string_label`, or the ingester dead-letters it. */
  data_kind?: string;
  scale?: number;
  offset?: number;
  is_cycle_counter?: boolean;
  /** The point's heartbeat in whole seconds (how long its last value reads as current): the API takes 1 to 604800;
   *  Connect sends 60 to 604800 (HEARTBEAT_SEC_MIN, HEARTBEAT_SEC_MAX in lib/iot/instrument-points). */
  max_silence_sec?: number;
}

/**
 * `POST /v1/things/{gw}/instruments`, v2 (multi-protocol) body: the device's
 * link `{driver, params}` plus per-point addresses, validated against the
 * driver registry. A 422 carries `fields[]` pointing at the offending params
 * (see `fieldProblemsOf` in lib/api/error).
 */
export interface CreateInstrumentV2Body {
  name: string;
  equipment_type?: string;
  /** The default for points that set no `poll_sec` of their own (30 when absent too). */
  poll_sec?: number;
  link: ThingLink;
  params: InstrumentPointV2[];
}

/**
 * `PATCH /v1/things/{gw}/instruments/{inst}`. Every field optional. `params` is
 * the complete new point list (v2 shape): points match existing tags by metric,
 * points left out are deleted. In `link`, a secret left out keeps the stored
 * one and `""` clears it; changing the driver requires `params`.
 */
export interface InstrumentPatchBody {
  name?: string;
  equipment_type?: string;
  /** Sets EVERY point's poll interval (before any `params[i].poll_sec`, which wins). */
  poll_sec?: number;
  link?: ThingLink;
  params?: InstrumentPointV2[];
}

/** `PATCH /v1/things/{gw}/instruments/{inst}` response. */
export interface InstrumentPatched {
  instrument_id: string;
  driver?: string | null;
  points_updated: number;
  points_created: number;
  points_deleted: number;
  secrets_set: string[];
  config_pushed: boolean;
}

/** Why a device on a gateway cannot use a driver (`Driver.unavailable_reason`). */
export type DriverUnavailableReason =
  | "not_in_product"
  | "planned"
  | "no_birth"
  | "no_serial_port"
  | "driver_not_advertised"
  | "firmware_too_old";

/**
 * Which point picker a driver gets. `block-scan` (S7, MELSEC) arrives with the
 * drivers-beta backend; a kind this build does not know is treated as manual
 * entry (`none`).
 */
export type DriverBrowseKind = "register-range" | "node-tree" | "tag-list" | "block-scan" | "none";

/** A JSON Schema object as the driver registry serves it (2020-12, plus x-ui-* hints). */
export type JsonSchema = Record<string, unknown>;

/** One field driver (`GET /v1/things/drivers` → `Driver`). */
export interface DriverInfo {
  id: string;
  label: string;
  medium: "ethernet" | "serial" | (string & {});
  /** Ethernet drivers: the port discover probes. */
  default_port?: number;
  status: "ga" | "beta" | "planned" | (string & {});
  browse: DriverBrowseKind | (string & {});
  data_types: PointDataType[];
  /** Link params that are write-only (never returned). */
  secrets?: string[];
  link_schema: JsonSchema;
  address_schema: JsonSchema;
  available: boolean;
  unavailable_reason?: DriverUnavailableReason | (string & {}) | null;
}

/** `GET /v1/things/drivers[?gateway_id=]`. */
export interface DriverList {
  /** Present when gateway_id was given. */
  gateway?: {
    id: string;
    product_id: string | null;
    capabilities_at: string | null;
    online: boolean | null;
  };
  drivers: DriverInfo[];
}

/** `POST /v1/things/{gw}/browse` body. `link` carries secrets inline (used for this read, never stored). */
export interface BrowseRequestBody {
  driver: string;
  link: Record<string, unknown>;
  /** modbus: {space, start, count}; opcua: {node_id}; enip: {prefix}; s7 / melsec: the block-scan cursor. Empty = the first page / root. */
  cursor?: Record<string, unknown>;
}

/** One browse entry: a point (with `address`) or a branch to descend (`has_children` + `cursor`). */
export interface BrowseEntry {
  name: string;
  address?: Record<string, unknown>;
  data_type?: string;
  value?: unknown;
  has_children?: boolean;
  cursor?: Record<string, unknown>;
}

/** `POST /v1/things/{gw}/browse` response: one page; `next` is absent when done. */
export interface BrowseResult {
  entries: BrowseEntry[];
  next?: Record<string, unknown> | null;
}

/** Response of the fire-and-ack command emitter (measure): 202 + the published topic. */
export interface CommandPublished {
  command_id: string;
  thing_id: string;
  topic: string;
  status: string;
  version?: string;
}

/**
 * `POST /v1/things/{gw}/discover` body. Every field is optional: `{}` sweeps the
 * gateway's own network (the /24 of its plant-LAN interface). A range must be
 * private (10/8, 172.16/12, 192.168/16) or loopback (127/8) and no wider than a
 * /20; the backend 400s `invalid_scan_target` otherwise.
 */
export interface DiscoverScanBody {
  /** A CIDR range ("192.168.1.0/24") or one IP. Omit for the gateway's own network. */
  cidr?: string;
  /** Modbus TCP port probed on each host (default 502). `ports["modbus-tcp"]` wins; when both are sent they must agree. */
  port?: number;
  /** Modbus unit ids tried per host (default [1], at most 16). */
  unit_ids?: number[];
  /**
   * Ethernet drivers to probe, each on its default port (v2 gateways). Omitted
   * or empty = modbus-tcp only, which is all a pre-v2 gateway can do.
   */
  drivers?: string[];
  /**
   * Per-driver scan ports: driver id => 1 to 4 TCP ports that REPLACE that
   * driver's default ports (e.g. `{ enip: [44819] }`). Every key must be a
   * probed driver. Omit it (or a driver) for the defaults; a gateway that
   * predates it ignores it and scans its defaults. A server that predates it
   * 400s `invalid_body` (unknown field "ports").
   */
  ports?: Record<string, number[]>;
}

/**
 * One device that answered a discover probe (v2 `DiscoverHit`). `identity` is
 * what the driver reported: vendor, product, endpoint (OPC UA), unit_id
 * (Modbus), rack / slot (S7), sample_value, ...
 */
export interface DiscoverHit {
  ip: string;
  port: number;
  driver: string;
  identity?: Record<string, string> | null;
}

/**
 * One host that had the probed TCP port open during a discover sweep (the
 * gateway's discovery `Found`). `speaks_modbus` says whether a Modbus unit
 * answered; `unit_id` is the unit that did (0 when none did).
 */
export interface ScannedHost {
  host: string;
  port: number;
  tcp_open: boolean;
  speaks_modbus: boolean;
  unit_id: number;
  /** Signed int16 of register 0, or null when the host only answered with an exception. */
  sample_value: number | null;
  /** Register space the sample came from: "holding" | "input" | "". */
  sample_from: string;
  /** Last probe error (e.g. a Modbus exception), else "". */
  error: string;
}

/**
 * `POST /v1/things/{gw}/discover` response. A current server always sends
 * `hits`; a pre-v2 gateway's own `found` list is passed through as well (it
 * also lists hosts that had the port open but gave no Modbus reply).
 */
export interface DiscoverScanResult {
  hits?: DiscoverHit[];
  found?: ScannedHost[];
  /** True when the sweep ran out of time: the list is real but may be incomplete. */
  partial?: boolean;
}

/** A contiguous block of addresses in one Modbus register space. */
export interface SniffScanRange {
  start: number;
  count: number;
}

/**
 * `POST /v1/things/{gw}/sniff` body: read one device's registers through the
 * gateway. `host` is a single private or loopback IP. With no range the backend
 * reads holding registers 0..63. At most 2000 addresses per request, and
 * `block_size` (addresses per Modbus read) at most 125.
 */
export interface SniffScanBody {
  host: string;
  /** TCP port (default 502). */
  port?: number;
  /** Modbus unit id, 1..247 (default 1). */
  unit_id?: number;
  holding?: SniffScanRange;
  input?: SniffScanRange;
  coils?: SniffScanRange;
  discretes?: SniffScanRange;
  block_size?: number;
}

/** One address a sniff read (the gateway's discovery `Cell`). */
export interface SniffScanCell {
  address: number;
  /** "holding" | "input" | "coils" | "discretes". */
  space: string;
  ok: boolean;
  /** e.g. "0x1F40" (registers only). */
  raw_hex: string;
  u16: number | null;
  i16: number | null;
  /** Coil / discrete value (bit spaces only). */
  bit: boolean | null;
  error: string;
}

/** `POST /v1/things/{gw}/sniff` response: the gateway's own answer, relayed. */
export interface SniffScanResult {
  host: string;
  port: number;
  unit_id: number;
  cells: SniffScanCell[];
}

/**
 * One VSync equipment-model template (`GET /v1/things/equipment-templates`) —
 * a model's Modbus transport + register map, derived from Versatile's fielded
 * fleet. Params map 1:1 to {@link InstrumentParamInput}, so picking a template
 * prefills the "Add instrument" form with real register addresses.
 */
export interface EquipmentTemplate {
  equipment_type: string;
  display_name: string;
  description: string;
  machines: number; // fielded units backing this template
  protocol: "rtu" | "tcp";
  slave_id: number;
  default_poll_sec: number;
  params: Array<InstrumentParamInput & { data_type: "int16" | "int32" | "float32" | "double" }>;
}

/**
 * One known point map (`GET /v1/things/point-maps`): a machine's points for
 * one field driver, without the points themselves. `products` are the device
 * product names (as a scan reports `identity.product`) it was built from.
 */
export interface PointMapSummary {
  id: string;
  driver: string;
  title: string;
  vendor: string;
  model: string;
  description: string;
  products: string[];
  point_count: number;
}

/** The most tags `GET /v1/things/{id}/tags` answers in one page (the server's limit cap, httpx.LimitOffset). */
export const THING_TAGS_PAGE_MAX = 200;

/** One page of a Thing's tags; `total` counts every tag that matches. */
export interface ThingTagPage {
  items: ThingTag[];
  total: number;
  limit: number;
  offset: number;
}

/** One walk through a Thing's tags (things.allTags), and whether it holds together. */
async function walkThingTags(id: string): Promise<{ tags: ThingTag[]; whole: boolean }> {
  const tags: ThingTag[] = [];
  let total: number | null = null;
  let steady = true;
  for (;;) {
    const page = await things.tags(id, { limit: THING_TAGS_PAGE_MAX, offset: tags.length });
    if (total !== null && page.total !== total) steady = false;
    total ??= page.total;
    tags.push(...page.items);
    if (page.items.length === 0 || tags.length >= page.total) break;
  }
  const whole = steady && tags.length === total && new Set(tags.map((t) => t.id)).size === tags.length;
  return { tags, whole };
}

/** The server's largest page of Things (httpx.LimitOffset). */
export const THINGS_PAGE_MAX = 200;
/** listAll's bound: 25 pages, 5,000 Things. */
export const THINGS_LIST_MAX_PAGES = 25;

export const things = {
  /**
   * List things. By default returns EVERY thing in the org (gateways +
   * sensor children + standalone). `kind` narrows to one thing kind
   * (e.g. "gateway" for the appliance-only view).
   */
  list: (params?: { site_id?: string; gateway_id?: string; kind?: string; product?: string; limit?: number; offset?: number }) =>
    api.get<ThingList>(`/v1/things${qs(params)}`),
  get: (id: string) => api.get<Thing>(`/v1/things/${id}`),
  /**
   * Every Thing that matches, past the server's page (50 unless asked, 200 at most): pages of 200 until a short
   * page, at most THINGS_LIST_MAX_PAGES. ListThings' `total` is the page's length, so a single page of 200 used to
   * look like all of them, and a plant's gateways past the 200th Thing (gateways and instruments together) were not
   * on the Gateways page. A Thing on two pages (the list is ordered by name) is kept once.
   */
  listAll: async (params?: { site_id?: string; gateway_id?: string; kind?: string; product?: string }): Promise<ThingList> => {
    const all = await collectAllPages<Thing>(
      (offset, limit) => things.list({ ...params, limit, offset }),
      THINGS_PAGE_MAX,
      THINGS_LIST_MAX_PAGES,
    );
    const seen = new Set<string>();
    const items = all.items.filter((t) => !seen.has(t.id) && seen.add(t.id));
    return { items, total: items.length, limit: items.length, offset: 0 };
  },
  /**
   * Every gateway's reading rate (the last 60 s) and its last 20 minutes a minute at a time, children rolled into
   * their gateway: `GET /v1/things/rates`, one read for the org (the list has no per-row rate since roadmap perf
   * #8). The server keeps an org's answer 10 s; an older API answers 400 invalid_thing_id (its /{thing_id} route).
   */
  rates: () => api.get<ThingRates>("/v1/things/rates"),
  /**
   * List a Thing's tags — `GET /v1/things/{id}/tags` (delegates server-side to
   * ListTags with device_id=id, org-scoped), ordered by name. ONE PAGE: the
   * server answers 50 unless `limit` asks for more (at most
   * THING_TAGS_PAGE_MAX); `offset` steps through, `q` filters by name and
   * `total` counts every match. A caller that needs the whole set uses
   * allTags.
   */
  tags: (id: string, page?: { limit?: number; offset?: number; q?: string }) =>
    api.get<ThingTagPage>(
      `/v1/things/${id}/tags${qs(page ? { limit: page.limit, offset: page.offset, q: page.q?.trim() } : undefined)}`,
    ),
  /**
   * EVERY tag of a Thing, page by page (THING_TAGS_PAGE_MAX each) until
   * `total` is reached. For callers that act on the whole set: the Modbus tag
   * editor, whose save replaces the set (seeded from one page of 50 it dropped
   * the rest), connect's bindings, and the reports' tag pickers. Stops at an
   * empty page whatever `total` says.
   *
   * A tag added or removed between two pages shifts the next page, so a walk
   * can skip or repeat a tag, and the editor's save would then drop the one
   * skipped. A walk holds together when every page gave the same total, it
   * read that many tags, and no id repeats. One that does not is walked once
   * more; a second that does not is refused (#1288 review).
   */
  allTags: async (id: string): Promise<ThingTag[]> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const walk = await walkThingTags(id);
      if (walk.whole) return walk.tags;
    }
    throw new Error("The instrument's tags changed while they were being read. Try again.");
  },
  /**
   * Per-instrument raw shift-readings sheet — `GET /v1/things/{id}/readings-sheet`.
   * One row per cycle (reading timestamp), a column per tag, optionally one
   * shift of the plant's calendar (`shift_label`, cut on the plant's clock by
   * the server). Backs the Shift Readings report panel.
   */
  readingsSheet: (id: string, params?: ReadingsSheetParams) =>
    api.get<ShiftReadingsSheet>(`/v1/things/${id}/readings-sheet${qs(params)}`),
  /**
   * The gateway-product catalogue (P6.3). Drives the Step-1 product picker
   * in the Add-a-Thing dialog and the field-protocol display on the Things
   * page. Each product lists the field drivers it may run (`drivers`; empty
   * for MQTT-native products such as generic-mqtt). The catalogue is the
   * server's, so no product list is kept here.
   */
  products: () => api.get<{ products: ThingProduct[] }>("/v1/things/products"),
  /**
   * The VSync equipment-template catalog (14 canonical models with real register
   * maps, derived from Versatile's fielded fleet). Prefills the "Add instrument"
   * picker — a chosen template's params drop straight into a createInstrument call.
   */
  equipmentTemplates: () =>
    api.get<{ templates: EquipmentTemplate[]; total: number }>("/v1/things/equipment-templates"),
  /**
   * The known point maps a device on `driver` can load (any protocol; the two
   * Modbus drivers load each other's). Backs "Load a known map" on the
   * Connect Points step.
   */
  pointMaps: (driver: string) =>
    api.get<{ maps: PointMapSummary[]; total: number }>(`/v1/things/point-maps${qs({ driver })}`),
  /**
   * One known point map: the sahas-forge.point-map v1 document itself, read
   * with the same parser as an imported file (lib/iot/point-map-io).
   */
  pointMap: (id: string) => api.get<Record<string, unknown>>(`/v1/things/point-maps/${encodeURIComponent(id)}`),
  /**
   * Create a Thing (P6.3): pick a gateway product, name it, flip a per-Thing
   * Simulate toggle, and author its tag rows. Returns the Thing + tag_count.
   */
  create: (body: CreateThingBody) => api.post<CreatedThing>("/v1/things", body),
  /**
   * Patch a Thing (P-wave3): rename it and/or flip its per-Thing Simulate
   * toggle. `PATCH /v1/things/{id}` body `{name?, simulate?}` → 200 Thing.
   * A gateway's `product_id` can also be switched (e.g. modbus-tcp →
   * gatewaypro after a GatewayPro upgrade; needs gateway.commission and a
   * server with that change): a 422 `unknown_product`,
   * `product_not_changeable` or `product_drivers_in_use` carries an
   * operator-ready `detail`.
   * A gateway's `update_fetch` (migration 0262) sets when it may fetch update
   * packages: `{mode, kbps}` (kbps 0 or null: the default rate); the answer's
   * `update_fetch.sent` says the gateway was told at once.
   * The caller should refresh the things query so the row reflects the edit.
   */
  update: (
    id: string,
    body: {
      name?: string;
      simulate?: boolean;
      product_id?: string;
      update_fetch?: { mode: UpdateFetchMode; kbps?: number | null };
    },
  ) => api.patch<Thing>(`/v1/things/${id}`, body),
  /**
   * Delete a Thing. The backend cascades the Thing's children + tags and
   * returns 204 No Content. The caller should refresh the things query.
   * This is the unified-surface path the Gateways page uses for both single
   * and bulk delete.
   */
  remove: (id: string) => api.delete<void>(`/v1/things/${id}`),
  /** Mint a one-time claim code for an existing thing (gateway onboarding). */
  claim: (id: string) =>
    api.post<{ claim_code: string; gateway_id: string; expires_at: string }>(
      `/v1/things/${id}/claim`,
    ),
  /**
   * Reset / re-provision a device: revoke its cert + mint a FRESH claim (+
   * optionally unlink its MAC) so it re-onboards. Distinct from {@link claim}
   * (which only mints) — this rotates the device identity. Returns the new code.
   */
  reprovision: (id: string, body?: { revoke?: boolean; unlink_mac?: boolean }) =>
    api.post<{
      claim_code: string;
      gateway_id: string;
      expires_at: string;
      revoked: boolean;
      mac_unlinked: boolean;
    }>(`/v1/things/${id}/reprovision`, body ?? {}),
  /**
   * Add an instrument to a VSync gateway — `POST /v1/things/{gw}/instruments`.
   * Creates a CHILD sensor Thing (the instrument) with its Modbus register map,
   * assigns the next machine_ref, and chains a config_push so an online device
   * starts polling it. The multi-instrument "Add instrument" action — a gateway
   * hosts many of these (replaces the old single-template-onto-the-gateway flow).
   */
  createInstrument: (gatewayId: string, body: CreateInstrumentBody) =>
    api.post<CreatedInstrument>(`/v1/things/${gatewayId}/instruments`, body),
  /**
   * Add an instrument with the v2 (multi-protocol) body: `link` {driver, params}
   * plus a per-point `address`. The driver must be in the gateway's product,
   * released, and (for non-Modbus drivers) advertised by the gateway's birth.
   * Chains a config push like the v1 create.
   */
  createInstrumentV2: (gatewayId: string, body: CreateInstrumentV2Body) =>
    api.post<CreatedInstrument>(`/v1/things/${gatewayId}/instruments`, body),
  /**
   * Edit an instrument's name, poll rate, link and points:
   * `PATCH /v1/things/{gw}/instruments/{inst}`. A kept point keeps its tag id,
   * bindings and history; a point left out of `params` is deleted. Chains a
   * config push.
   */
  patchInstrument: (gatewayId: string, instrumentId: string, body: InstrumentPatchBody) =>
    api.patch<InstrumentPatched>(`/v1/things/${gatewayId}/instruments/${instrumentId}`, body),
  /** Push the gateway's current device config now (every create / edit already chains one). */
  configPush: (gatewayId: string) =>
    api.post<CommandPublished>(`/v1/things/${gatewayId}/config_push`, {}),
  /**
   * The field-driver registry with each driver's JSON Schemas. With a gateway,
   * `available` / `unavailable_reason` say which drivers a new device on that
   * gateway may use (its product and its last birth decide).
   */
  listDrivers: (gatewayId?: string) =>
    api.get<DriverList>(`/v1/things/drivers${qs(gatewayId ? { gateway_id: gatewayId } : undefined)}`),
  /**
   * One page of a device's address space, read through the gateway:
   * `POST /v1/things/{gw}/browse`. Only gateways whose birth advertises the
   * driver with config schema v2 answer (409 `gateway_cannot_browse`
   * otherwise); 422 `browse_unsupported` = enter addresses by hand. Can take
   * up to about 25 s, like discover.
   */
  browse: (gatewayId: string, body: BrowseRequestBody) =>
    api.post<BrowseResult>(`/v1/things/${gatewayId}/browse`, body),
  /**
   * Take one immediate Modbus reading now — `POST /v1/things/{gw}/measure`.
   * `device_id` (an instrument child slug) scopes it to one instrument; omit to
   * measure every instrument on the gateway. 202 published; the device acks
   * asynchronously on its cmdack topic.
   */
  measure: (gatewayId: string, body?: { device_id?: string }) =>
    api.post<CommandPublished>(`/v1/things/${gatewayId}/measure`, body ?? {}),
  /**
   * Sweep a claimed gateway's plant network for Modbus devices, relayed through
   * the cloud: `POST /v1/things/{gw}/discover`. The scan rides the gateway's own
   * command channel and the answer returns on its ack, so it can take up to
   * about 25 s. 409 = the gateway is not claimed yet; 504 = it did not answer.
   * Same gateway.commission gate as config_push.
   */
  discover: (gatewayId: string, body?: DiscoverScanBody) =>
    api.post<DiscoverScanResult>(`/v1/things/${gatewayId}/discover`, body ?? {}),
  /**
   * Read one device's registers through a claimed gateway, relayed through the
   * cloud: `POST /v1/things/{gw}/sniff`. Returns the raw cells (nothing is
   * guessed). Can take up to about 25 s. 424 `scan_failed` = the device did not
   * answer (the gateway's reason is in the detail); 504 = the gateway did not.
   */
  sniff: (gatewayId: string, body: SniffScanBody) =>
    api.post<SniffScanResult>(`/v1/things/${gatewayId}/sniff`, body),
  /**
   * Adopt a simulated factory source into the active org by claim code
   * (Phase 2 ownership transfer). Distinct from `claim` above — this
   * reassigns a pre-built mock gateway+children to the caller's org.
   */
  adopt: (claimCode: string, siteId?: string) =>
    api.post<{
      gateway_id: string;
      site_id: string;
      device_count: number;
      claimed: boolean;
    }>("/v1/things/claim", { claim_code: claimCode, ...(siteId ? { site_id: siteId } : {}) }),
};

/**
 * Mapper exists because GatewayRecord (UI) predates the contract — the
 * server's Thing has no msg-rate / twin-binding fields yet, and the list
 * endpoint omits the cert/last_seen fields (only the gateway read carries
 * them), so those degrade to sensible defaults here. The `tags` count is
 * filled in by the Things page from the org tag catalogue (a Thing row
 * carries no tag count), so it defaults to 0 here.
 *
 * P6.6: the displayed *field protocol* is no longer a hardcoded
 * "modbus-tcp" — we carry the Thing's `product_id` through and let the
 * consumer (Things page / detail sheet) resolve it against the products
 * catalogue, with MQTT shown as the transport. `protocol` is left blank
 * here (resolved at render); `productId` is the source of truth.
 *
 * Plan to delete in a later slice when the Things table + the
 * `/v1/things/{id}/tags` heartbeat feed read the server shapes directly.
 */
export function toThingRecord(t: Thing): GatewayRecord {
  return {
    id: t.id,
    name: t.name,
    site: t.site_id,
    slug: t.slug,
    // P6.6: protocol is derived from productId at render time against the
    // products catalogue — no more fake "modbus-tcp" default.
    protocol: "",
    productId: t.product_id ?? null,
    simulate: t.simulate ?? undefined,
    isSimulated: t.is_simulated ?? undefined,
    vendorName: t.vendor_name ?? undefined,
    tags: t.tag_count ?? 0,
    msgPerSec: t.msg_per_sec ?? 0,
    msgPerSecHistory: [],
    lastSeen: t.last_seen ?? t.updated_at,
    status: deriveStatus(t.last_seen, t.online),
    certExpiresAt: t.cert_expires_at ?? "",
    twinEntityId: t.twin_entity_id ?? null,
    twinEntityName: t.twin_entity_name ?? null,
  };
}

/**
 * A gateway's liveness. A gateway that announces itself (GatewayPro v0.3+)
 * carries `online` from its birth and offline Last Will, which is the truth:
 * `last_seen` does not move on an idle gateway with no devices. Older gateways
 * never set it, so they keep the last_seen recency heuristic.
 */
export function deriveStatus(
  lastSeen?: string | null,
  online?: boolean | null,
): GatewayRecord["status"] {
  if (online === true) return "online";
  if (online === false) return "offline";
  if (!lastSeen) return "unknown";
  const ageMs = Date.now() - new Date(lastSeen).getTime();
  if (Number.isNaN(ageMs)) return "unknown";
  if (ageMs < 2 * 60_000) return "online";
  if (ageMs < 15 * 60_000) return "warning";
  return "offline";
}
